import { proxy, snapshot, useSnapshot } from "@web-monorepo/hooks";

export type ToolkitNativeActionError = {
  rawAction: string;
  message: string;
};

export type ToolkitStoreState = {
  isRuntimeReady: boolean;
  isInteractable: boolean;
  nativeActionQueue: string[];
  nativeActionErrors: ToolkitNativeActionError[];
  webviewPlatform: "ios" | "android" | "web" | null;
  webviewBuildNumber: number | null;
};

function createInitialState(): ToolkitStoreState {
  return {
    isRuntimeReady: false,
    isInteractable: false,
    nativeActionQueue: [],
    nativeActionErrors: [],
    webviewPlatform: null,
    webviewBuildNumber: null,
  };
}

const store = proxy<ToolkitStoreState>(createInitialState());

export const toolkitActions = {
  setRuntimeReady(isRuntimeReady: boolean) {
    store.isRuntimeReady = isRuntimeReady;
  },

  setInteractable(isInteractable: boolean) {
    store.isInteractable = isInteractable;
  },

  enqueueNativeAction(rawAction: string) {
    store.nativeActionQueue.push(rawAction);
  },

  drainNativeActionQueue() {
    const queuedActions = [...store.nativeActionQueue];
    store.nativeActionQueue = [];
    return queuedActions;
  },

  recordNativeActionError(rawAction: string, error: unknown) {
    store.nativeActionErrors.push({
      rawAction,
      message: error instanceof Error ? error.message : String(error),
    });
  },

  setWebviewPlatform(platform: ToolkitStoreState["webviewPlatform"], buildNumber: number | null = null) {
    store.webviewPlatform = platform;
    store.webviewBuildNumber = buildNumber;
  },
};

export function useToolkitStore() {
  return useSnapshot(store);
}

export function getToolkitStoreState() {
  return snapshot(store);
}

export function resetToolkitStore() {
  Object.assign(store, createInitialState());
}
